import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import CompetitionCard from "@/components/competition-card";
import { LoadingSpinner } from "@/components/ui/loading";
import { Search, Filter, Trophy, Clock } from "lucide-react";
import { updateSEOTags, seoConfigs } from "@/utils/seo";

export default function Competitions() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // SEO Optimization
  useEffect(() => {
    updateSEOTags(seoConfigs.competitions);
  }, []);

  // Fetch active competitions
  const { data: activeCompetitions, isLoading: isLoadingActive } = useQuery({
    queryKey: ["/api/competitions"],
  });

  // Fetch past competitions  
  const { data: pastCompetitions, isLoading: isLoadingPast } = useQuery({
    queryKey: ["/api/competitions/past"],
  });

  const isLoading = isLoadingActive || isLoadingPast;

  // Ensure arrays exist for safe rendering
  const activeCompetitionsArray = Array.isArray(activeCompetitions) ? activeCompetitions : [];
  const pastCompetitionsArray = Array.isArray(pastCompetitions) ? pastCompetitions : [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      {/* Header - Mobile Responsive */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
              All Competitions
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-blue-100 px-4">
              Discover amazing prizes and enter competitions that match your interests
            </p>
          </div>
        </div>
      </section>

      {/* Filters - COMMENTED OUT */}
      {false && (
        <section className="py-8 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search competitions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-center">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="gaming">Gaming</SelectItem>
                    <SelectItem value="lifestyle">Lifestyle</SelectItem>
                    <SelectItem value="sports">Sports</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="ending-soon">Ending Soon</SelectItem>
                    <SelectItem value="most-popular">Most Popular</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Competitions with Tabs */}
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Competitions
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              Daily, weekly, and monthly chances to win big!
            </p>
          </div>

          <Tabs defaultValue="current" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="current" data-testid="tab-current-competitions">
                Current Competitions ({activeCompetitionsArray.length})
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed-competitions">
                Completed Competitions ({pastCompetitionsArray.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="current" data-testid="content-current-competitions">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {isLoading ? (
                  <div className="col-span-full text-center py-8 sm:py-12">
                    <LoadingSpinner size="lg" />
                    <p className="text-gray-600 dark:text-gray-400 mt-3 sm:mt-4 text-sm sm:text-base">Loading current competitions...</p>
                  </div>
                ) : activeCompetitionsArray.length === 0 ? (
                  <div className="col-span-full text-center py-8 sm:py-12">
                    <Trophy className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">No current competitions</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Check back later for new competitions!</p>
                  </div>
                ) : (
                  activeCompetitionsArray.map((competition: any) => (
                    <CompetitionCard key={competition.id} competition={competition} />
                  ))
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="completed" data-testid="content-completed-competitions">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {isLoading ? (
                  <div className="col-span-full text-center py-8 sm:py-12">
                    <LoadingSpinner size="lg" />
                    <p className="text-gray-600 dark:text-gray-400 mt-3 sm:mt-4 text-sm sm:text-base">Loading completed competitions...</p>
                  </div>
                ) : pastCompetitionsArray.length === 0 ? (
                  <div className="col-span-full text-center py-8 sm:py-12">
                    <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">No completed competitions</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Completed competitions will appear here.</p>
                  </div>
                ) : (
                  pastCompetitionsArray.map((competition: any) => (
                    <CompetitionCard key={competition.id} competition={competition} />
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
}
